import { clamp } from '@library/utils'
import { useCallback, useEffect, useState } from 'react'

const calculateRange = (data: unknown[], elementsPerPage: number): number =>
  Math.ceil(data.length / elementsPerPage)

const sliceData = <Type>(data: Type[], page: number, elementsPerPage: number): Type[] =>
  data.slice((page - 1) * elementsPerPage, page * elementsPerPage)

/**
 * Хук для упрощенной пагинации
 *
 * Управляет отображаемыми данными и текущей страницей
 *
 * Страницы индексируются от 1 до ceil(data.length / elementsPerPage)
 *
 * Соответственно страница 0 невалидна, и будет переведена на первую
 *
 * Все страницы, большие максимальной, будут переведены на максимальную
 *
 * @param data исходные данные
 * @param elementsPerPage по сколько элементов должно находиться на странице
 * @returns текущее состояние пагинации
 */
export const usePagination = <Type>(data: Type[], elementsPerPage: number) => {
  const [currentPage, setCurrentPage] = useState(1)
  const pageCount = calculateRange(data, elementsPerPage)

  const clampPage = useCallback((value: number) => clamp(value, 1, pageCount), [pageCount])

  const handleChangePage = (pageNumber: number) => {
    setCurrentPage(clampPage(pageNumber))
  }

  useEffect(() => {
    setCurrentPage(clampPage)
  }, [clampPage])

  return {
    pageData: sliceData(data, currentPage, elementsPerPage),
    pageCount,
    currentPage,
    handleChangePage,
  }
}
